import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  activeCallDockRecipe,
  activeCallPillInnerRecipe,
  activeCallPillRecipe,
} from '../recipes/mobile.recipe';
import { StatusChip } from './StatusChip';
import type { StatusTone } from './status-tone';
import { Text } from './Text';

export interface ActiveCallPillProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Where the call is: the first line of the address, "La Jolla Cir". */
  address: string;
  /** Where it has got to, in words: "Dispatching", "En route". */
  status: string;
  /** The chip's colour, usually the job phase: `phase-en-route`. */
  statusTone?: StatusTone;
  /** When the call entered this status; the chip counts up from it. */
  since?: Date | string | number;
  /**
   * How many calls the member is on. Above one, the pill says so —
   * "You have 2 active calls" — instead of naming the first, and the screen
   * sends the tap to the calls list rather than to one call.
   */
  count?: number;
}

/**
 * The call you are on, floating just above the tab bar until it is done:
 * "La Jolla Cir  Dispatching • 4:21". Tapping it goes to the call.
 *
 *   <ActiveCallPill address="La Jolla Cir" status="Dispatching"
 *     statusTone="phase-dispatched" since={call.startedAt}
 *     onClick={() => navigate(`/calls/${call.id}`)} />
 *
 * The Figma's Status Context: a white pill in a faint wash, in the float
 * material, docked over the tab bar on every tab so the live call is never
 * more than a thumb away. It places itself, as the tab bar does.
 */
export const ActiveCallPill = React.forwardRef<
  HTMLButtonElement,
  ActiveCallPillProps
>(function ActiveCallPill(
  {
    address,
    status,
    statusTone = 'neutral',
    since,
    count = 1,
    className,
    ...props
  },
  ref
) {
  const several = count > 1;
  return (
    <div className={activeCallDockRecipe()}>
      <button
        ref={ref}
        type="button"
        className={cn(activeCallPillRecipe(), className)}
        {...props}
      >
        <span
          className={activeCallPillInnerRecipe({
            end: several ? 'text' : 'chip',
          })}
        >
          {several ? (
            <Text as="span" variant="body-lg-strong" truncate>
              You have {count} active calls
            </Text>
          ) : (
            <>
              <Text as="span" variant="h3" truncate>
                {address}
              </Text>
              <StatusChip label={status} tone={statusTone} since={since} />
            </>
          )}
        </span>
      </button>
    </div>
  );
});
